/**
 * Outcome Card Component
 * 
 * Displays projected outcomes including financial projections, resource requirements,
 * key benefits, and risk indicators.
 */

import React, { useState, useEffect } from 'react';
import { Card, Tag, Space, Spin, Alert, Typography, List, Statistic, Row, Col, Divider } from 'antd';
import { 
  DollarOutlined,
  UserOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { CardSectionProps } from '../../../types/compositeCards';
import { apiClient } from '../../../services/apiClient';
import styles from './OutcomeCard.module.css';

const { Text, Title } = Typography;

interface FinancialProjections {
  implementation_cost: {
    low: number;
    high: number;
    currency: string;
  };
  revenue_increase: {
    low: number;
    high: number;
    currency: string;
  };
  net_earnings_impact: {
    low: number;
    high: number;
    currency: string;
  };
  roi: {
    low: number;
    high: number;
    unit: string;
  };
}

interface ResourceRequirement {
  phase: string;
  months: string;
  resources: string;
  description: string;
}

interface RiskIndicator {
  type: string;
  severity: 'low' | 'medium' | 'high';
  description: string;
}

interface OutcomeData {
  objective: {
    id: string;
    name: string;
    description: string;
  };
  financial_projections: FinancialProjections;
  resource_requirements: ResourceRequirement[];
  key_benefits: string[];
  risk_indicators: RiskIndicator[];
  timeline_months: number;
}

export const OutcomeCard: React.FC<CardSectionProps> = ({ 
  card,
  onAction
}) => {
  const { data } = card;
  const [outcomeData, setOutcomeData] = useState<OutcomeData | null>(
    'outcome' in data ? (data.outcome as OutcomeData) : null
  );
  const [loading, setLoading] = useState(!outcomeData);
  const [error, setError] = useState<string | null>(null);

  const objectiveId = 'objectiveId' in data ? data.objectiveId : null;
  const timelineMonths = 'timelineMonths' in data ? data.timelineMonths : 6;

  useEffect(() => {
    if (!outcomeData && objectiveId) {
      void loadOutcomes();
    }
  }, [objectiveId, timelineMonths]);

  const loadOutcomes = async () => {
    if (!objectiveId) {
      setError('Objective ID required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/objectives/${objectiveId}/outcomes?timeline_months=${timelineMonths}`);
      setOutcomeData(response.data);
    } catch (err: any) {
      console.error('Failed to load outcomes:', err);
      setError(err.response?.data?.detail || 'Failed to load outcomes');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatRange = (low: number, high: number, formatter: (n: number) => string) => {
    return `${formatter(low)} - ${formatter(high)}`;
  };

  const getRiskColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'red';
      case 'medium':
        return 'orange';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Card className={styles.outcomeCard}>
        <Spin tip="Loading outcomes..." />
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={styles.outcomeCard}>
        <Alert message="Error" description={error} type="error" showIcon />
      </Card>
    );
  }

  if (!outcomeData) {
    return (
      <Card className={styles.outcomeCard}>
        <Alert message="No outcome data" type="info" />
      </Card>
    );
  }

  const { financial_projections, resource_requirements, key_benefits, risk_indicators, timeline_months } = outcomeData;

  return (
    <Card 
      className="outcome-card"
      title={
        <Space>
          <DollarOutlined />
          <span>Projected Outcomes: {timeline_months}-Month Timeline</span>
        </Space>
      }
    >
      {/* Financial Projections */}
      <div style={{ marginBottom: 24 }}>
        <Title level={5}>Financial Projections</Title>
        <Row gutter={16}>
          <Col span={12}>
            <Statistic
              title="Implementation Cost"
              value={formatRange(
                financial_projections.implementation_cost.low,
                financial_projections.implementation_cost.high,
                (n) => formatCurrency(n, financial_projections.implementation_cost.currency)
              )}
              prefix={<DollarOutlined />}
            />
          </Col>
          <Col span={12}>
            <Statistic
              title="Revenue Increase"
              value={formatRange(
                financial_projections.revenue_increase.low,
                financial_projections.revenue_increase.high,
                (n) => formatCurrency(n, financial_projections.revenue_increase.currency)
              )}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={12}>
            <Statistic
              title="Net Earnings Impact"
              value={formatRange(
                financial_projections.net_earnings_impact.low,
                financial_projections.net_earnings_impact.high,
                (n) => formatCurrency(n, financial_projections.net_earnings_impact.currency)
              )}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
          <Col span={12}>
            <Statistic
              title="ROI"
              value={formatRange(
                financial_projections.roi.low,
                financial_projections.roi.high,
                (n) => `${n.toFixed(0)}%`
              )}
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
        </Row>
      </div>

      <Divider />

      {/* Resource Requirements */}
      {resource_requirements.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <Title level={5}>
            <UserOutlined /> Resource Requirements
          </Title>
          <List
            dataSource={resource_requirements}
            renderItem={(item) => (
              <List.Item>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space>
                    <CalendarOutlined />
                    <Text strong>{item.phase}</Text>
                    <Tag>Months {item.months}</Tag>
                  </Space>
                  <Text>{item.resources}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>{item.description}</Text>
                </Space>
              </List.Item>
            )}
          />
        </div>
      )}

      <Divider />

      {/* Key Benefits */}
      {key_benefits.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <Title level={5}>
            <CheckCircleOutlined /> Key Benefits
          </Title>
          <List
            dataSource={key_benefits}
            renderItem={(item) => (
              <List.Item>
                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                <Text>{item}</Text>
              </List.Item>
            )}
          />
        </div>
      )}

      {/* Risk Indicators */}
      {risk_indicators.length > 0 && (
        <div>
          <Title level={5}>
            <WarningOutlined /> Risk Indicators
          </Title>
          <List
            dataSource={risk_indicators}
            renderItem={(item) => (
              <List.Item>
                <Space>
                  <WarningOutlined style={{ color: getRiskColor(item.severity) === 'red' ? '#ff4d4f' : '#faad14' }} />
                  <div>
                    <Text>{item.description}</Text>
                    <Tag color={getRiskColor(item.severity)} style={{ marginLeft: 8 }}>
                      {item.severity.toUpperCase()}
                    </Tag>
                  </div>
                </Space>
              </List.Item>
            )}
          />
        </div>
      )}
    </Card>
  );
};

